import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { formatMs } from '@flm9/i18n/labels';
import { useTimer, useTimerRun } from '../lib/timer/provider';
import { userErrorText } from '../lib/userErrorText';
import {
  loadPhoneTimerCalibration,
  micAvailable,
  PhoneTimerSource,
  type AppliedAudioConstraints,
  type PhoneTimerCalibration,
  type PhoneTimerLevel,
} from '../lib/timer/phone';
import { PhoneTimerCalibrationScreen } from './PhoneTimerCalibration';
import type { TimerStatus } from '@flm9/scoring';

export interface TimingPadHandle {
  /**
   * Persist any pending edits that haven't been saved yet (e.g. user
   * typed a value but hasn't blurred). Score.tsx awaits this before
   * confirming the round so the values land on the assignment before
   * it gets locked.
   */
  flush: () => Promise<void>;
}

interface Props {
  /**
   * Current values from the round (saved server-side after each tap).
   * Used for state hydration when the score screen is reopened, and synced
   * in when they change later (e.g. after a Bluetooth SG run, REF-7).
   */
  initialFirstShotMs?: number | null;
  initialElapsedMs?: number | null;
  /** Persist a partial set of timing values on the round. */
  onSave: (patch: { firstShotTimeMs?: number; elapsedTimeMs?: number }) => Promise<void>;
  /**
   * If true the section is expanded by default (speed-based schemas need
   * the data to compute hitFactor). When false the panel starts collapsed
   * — the timings are optional metadata for leaderboard views to consume.
   */
  required?: boolean;
  /** Stage's expected shot count — drives auto-stop in the SG-Timer mode. */
  shotCount?: number | undefined;
  /** Schema kind decides whether shot count is meaningful. IPSC is variable. */
  scoringSchemaKind?: 'ring_value' | 'hit_factor' | undefined;
  /** Opens the timer-debug screen for pairing / help. */
  onOpenTimerDebug?: (() => void) | undefined;
  /**
   * Gates the phone-mic timer tab. Only true on self-signed sessions —
   * the feature is beta and explicitly excluded from any session that
   * requires range-officer sign-off.
   */
  allowPhoneTimer?: boolean;
  /** Shooter this pad belongs to — the SG panel only mirrors a run started for them. */
  assignmentId: string;
}

type Phase = 'idle' | 'armed' | 'first-pending' | 'running' | 'stopped' | 'manual' | 'sg' | 'phone';

function fmt(ms: number | null | undefined): string {
  if (ms == null) return '–';
  const s = ms / 1000;
  return `${s.toFixed(2).replace('.', ',')} s`;
}

/**
 * Parse a user-typed seconds value into milliseconds. Accepts both `.`
 * and `,` as decimal separators (de-AT default), trims whitespace, treats
 * empty input as undefined.
 */
function parseSeconds(input: string): number | undefined {
  const v = input.trim().replace(',', '.');
  if (!v) return undefined;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 1000);
}

function msToInputString(ms: number | null | undefined): string {
  if (ms == null) return '';
  return (ms / 1000).toFixed(2).replace('.', ',');
}

/**
 * Touch-friendly stopwatch for the first-shot delay + total round time.
 *
 * Default flow: tap "Start" (3 s armed countdown to give the shooter time
 * to settle) → "Erster Schuss" tap on first noise → "Stop" tap on last
 * shot. Each tap calls onSave() so the partial state is durable; if the
 * referee navigates away and comes back the timing strip is restored.
 *
 * "Manuell" toggle exposes raw number inputs for cases where the referee
 * already stopwatched it on paper.
 */
export const TimingPad = forwardRef<TimingPadHandle, Props>(function TimingPad(
  {
    initialFirstShotMs,
    initialElapsedMs,
    onSave,
    required = true,
    shotCount,
    scoringSchemaKind,
    onOpenTimerDebug,
    allowPhoneTimer = false,
    assignmentId,
  },
  ref,
) {
  const isVariableShotCount =
    scoringSchemaKind === 'hit_factor' || shotCount == null || shotCount <= 0;
  const expectedShots = isVariableShotCount ? null : (shotCount ?? null);
  const timer = useTimer();
  const hasInitialData = initialFirstShotMs != null || initialElapsedMs != null;
  // Default to manual mode — most ranges use an external timer (CED,
  // PACT, etc.) and the referee just types in the values reported on
  // the timer display. Stoppuhr is the secondary mode.
  const [phase, setPhase] = useState<Phase>(
    initialFirstShotMs != null && initialElapsedMs != null ? 'stopped' : 'manual',
  );
  const [firstShotMs, setFirstShotMs] = useState<number | null>(initialFirstShotMs ?? null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(initialElapsedMs ?? null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [now, setNow] = useState(0);
  const [armCountdown, setArmCountdown] = useState(0);
  const [expanded, setExpanded] = useState<boolean>(required || hasInitialData);

  const startedAtRef = useRef<number | null>(null);
  const armDeadlineRef = useRef<number | null>(null);
  const animRef = useRef<number | null>(null);
  const [manualFirst, setManualFirst] = useState<string>(msToInputString(initialFirstShotMs));
  const [manualElapsed, setManualElapsed] = useState<string>(msToInputString(initialElapsedMs));

  // Animation loop while running / arming. Runs at ~60 fps via rAF and
  // updates the on-screen clock; stops when phase changes away.
  useEffect(() => {
    if (phase !== 'armed' && phase !== 'first-pending' && phase !== 'running') return;
    let mounted = true;
    const tick = () => {
      if (!mounted) return;
      if (phase === 'armed' && armDeadlineRef.current != null) {
        const left = armDeadlineRef.current - performance.now();
        setArmCountdown(left);
        if (left <= 0) {
          startedAtRef.current = performance.now();
          setPhase('first-pending');
        }
      } else if (startedAtRef.current != null) {
        setNow(performance.now() - startedAtRef.current);
      }
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => {
      mounted = false;
      if (animRef.current != null) cancelAnimationFrame(animRef.current);
    };
  }, [phase]);

  function arm() {
    armDeadlineRef.current = performance.now() + 3000;
    setArmCountdown(3000);
    setPhase('armed');
    setFirstShotMs(null);
    setElapsedMs(null);
    setNow(0);
  }

  async function recordFirstShot() {
    if (phase !== 'first-pending' || startedAtRef.current == null) return;
    const ms = Math.round(performance.now() - startedAtRef.current);
    setFirstShotMs(ms);
    setPhase('running');
    try {
      await onSave({ firstShotTimeMs: ms });
      setSaveError(null);
    } catch {
      setSaveError('Zeit konnte nicht gespeichert werden. Bitte erneut versuchen.');
    }
  }

  async function stopAndSave() {
    if (phase !== 'running' || startedAtRef.current == null) return;
    const ms = Math.round(performance.now() - startedAtRef.current);
    setElapsedMs(ms);
    setPhase('stopped');
    try {
      await onSave({ elapsedTimeMs: ms });
      setSaveError(null);
    } catch {
      setSaveError('Zeit konnte nicht gespeichert werden. Bitte erneut versuchen.');
    }
  }

  function reset() {
    startedAtRef.current = null;
    armDeadlineRef.current = null;
    setNow(0);
    setArmCountdown(0);
    setFirstShotMs(null);
    setElapsedMs(null);
    setPhase('idle');
  }

  // Hold refs to the *latest* typed values so the imperative flush()
  // exposed to Score.tsx can read them after blur events have lost focus.
  const manualFirstRef = useRef(manualFirst);
  const manualElapsedRef = useRef(manualElapsed);
  manualFirstRef.current = manualFirst;
  manualElapsedRef.current = manualElapsed;

  // Values saved elsewhere (the SG run above the keypad) arrive as new
  // props (REF-7). Synced instead of remounting, so a field the referee is
  // still typing in keeps its text: a field only follows the prop while it
  // still holds the last synced value. A running stopwatch keeps its clock.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  // The exact synced ms plus the (rounded) text shown for them: a field
  // counts as untouched while its text still equals that synced text, and
  // then saves the exact ms, not the rounded display value.
  const syncedRef = useRef({
    first: initialFirstShotMs ?? null,
    elapsed: initialElapsedMs ?? null,
    firstText: msToInputString(initialFirstShotMs),
    elapsedText: msToInputString(initialElapsedMs),
  });
  useEffect(() => {
    const first = initialFirstShotMs ?? null;
    const elapsed = initialElapsedMs ?? null;
    const prev = syncedRef.current;
    if (first === prev.first && elapsed === prev.elapsed) return;
    const next = { ...prev, first, elapsed };
    const timing = ['armed', 'first-pending', 'running'].includes(phaseRef.current);
    if (first !== prev.first) {
      if (!timing) setFirstShotMs(first);
      if (manualFirstRef.current === prev.firstText) {
        next.firstText = msToInputString(first);
        setManualFirst(next.firstText);
      }
    }
    if (elapsed !== prev.elapsed) {
      if (!timing) setElapsedMs(elapsed);
      if (manualElapsedRef.current === prev.elapsedText) {
        next.elapsedText = msToInputString(elapsed);
        setManualElapsed(next.elapsedText);
      }
    }
    syncedRef.current = next;
    if (next.first != null || next.elapsed != null) setExpanded(true);
  }, [initialFirstShotMs, initialElapsedMs]);

  async function saveManual() {
    const patch: { firstShotTimeMs?: number; elapsedTimeMs?: number } = {};
    // An untouched field keeps the exact synced value (the text is rounded).
    const synced = syncedRef.current;
    const fst =
      manualFirstRef.current === synced.firstText
        ? (synced.first ?? undefined)
        : parseSeconds(manualFirstRef.current);
    const elp =
      manualElapsedRef.current === synced.elapsedText
        ? (synced.elapsed ?? undefined)
        : parseSeconds(manualElapsedRef.current);
    if (fst !== undefined) patch.firstShotTimeMs = fst;
    if (elp !== undefined) patch.elapsedTimeMs = elp;
    if (Object.keys(patch).length === 0) return;
    if (patch.firstShotTimeMs !== undefined) setFirstShotMs(patch.firstShotTimeMs);
    if (patch.elapsedTimeMs !== undefined) setElapsedMs(patch.elapsedTimeMs);
    try {
      await onSave(patch);
      setSaveError(null);
    } catch (err) {
      setSaveError('Zeit konnte nicht gespeichert werden. Bitte erneut versuchen.');
      throw err;
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      flush: () => saveManual(),
    }),
    // saveManual closes over manualFirstRef/manualElapsedRef so it's stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (!expanded) {
    return (
      <section className="rounded-lg border border-zinc-800 bg-zinc-900/30 p-3">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex w-full items-center justify-between text-sm text-zinc-400 hover:text-zinc-200"
        >
          <span>
            ⏱ Zeitnahme
            <span className="ml-2 text-xs text-zinc-500">(optional)</span>
          </span>
          <span>+</span>
        </button>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-zinc-700 bg-zinc-900/40 p-3">
      <div className="mb-3 flex items-baseline justify-between">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-bold text-zinc-300">Zeitnahme</h3>
          {!required && <span className="text-xs text-zinc-500">(optional)</span>}
        </div>
        {!required && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            aria-label="Einklappen"
            className="text-xs text-zinc-500 hover:text-zinc-300"
          >
            −
          </button>
        )}
      </div>

      {saveError && (
        <p role="alert" className="mb-3 text-sm text-red-400">
          {saveError}
        </p>
      )}
      {/* Mode tabs — Manuell is the default since most ranges run with
          an external timer and the referee just types the readout.
          The Phone tab is only offered when the parent passes
          `allowPhoneTimer` (i.e. self-signed sessions). */}
      <div
        className={`mb-3 grid gap-1 rounded-lg bg-zinc-900 p-1 text-xs ${
          allowPhoneTimer ? 'grid-cols-4' : 'grid-cols-3'
        }`}
      >
        <button
          type="button"
          onClick={() => setPhase('manual')}
          className={`min-h-12 rounded-md px-3 py-2 font-medium transition-colors ${
            phase === 'manual' ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-400 hover:text-zinc-200'
          }`}
        >
          Manuell
        </button>
        <button
          type="button"
          onClick={() => setPhase('idle')}
          className={`min-h-12 rounded-md px-3 py-2 font-medium transition-colors ${
            phase !== 'manual' && phase !== 'sg' && phase !== 'phone'
              ? 'bg-zinc-700 text-zinc-100'
              : 'text-zinc-400 hover:text-zinc-200'
          }`}
        >
          Stoppuhr
        </button>
        <button
          type="button"
          onClick={() => setPhase('sg')}
          disabled={!timer.bleSupported}
          className={`min-h-12 rounded-md px-3 py-2 font-medium transition-colors ${
            phase === 'sg' ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-400 hover:text-zinc-200'
          } disabled:opacity-30`}
          title={timer.bleSupported ? 'SG Timer per Bluetooth' : 'Web Bluetooth nicht verfügbar'}
        >
          SG Timer
        </button>
        {allowPhoneTimer && (
          <button
            type="button"
            onClick={() => setPhase('phone')}
            disabled={!micAvailable()}
            className={`min-h-12 rounded-md px-3 py-2 font-medium transition-colors ${
              phase === 'phone' ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-400 hover:text-zinc-200'
            } disabled:opacity-30`}
            title={
              micAvailable()
                ? 'Telefon-Mikrofon als Schuss-Timer (Beta, nur Solo-Training)'
                : 'Mikrofon-API nicht verfügbar'
            }
          >
            🎙 Phone β
          </button>
        )}
      </div>

      {phase === 'sg' ? (
        <SgTimerPanel
          assignmentId={assignmentId}
          expectedShots={expectedShots}
          onOpenTimerDebug={onOpenTimerDebug}
          onLeave={() => setPhase('manual')}
          existingFirstShotMs={firstShotMs}
          existingElapsedMs={elapsedMs}
        />
      ) : phase === 'phone' ? (
        <PhoneTimerPanel
          expectedShots={expectedShots}
          onLeave={() => setPhase('manual')}
          onSave={onSave}
          existingFirstShotMs={firstShotMs}
          existingElapsedMs={elapsedMs}
          setFirstShotMs={setFirstShotMs}
          setElapsedMs={setElapsedMs}
        />
      ) : phase !== 'manual' ? (
        <>
          {/* Big clock display */}
          <div className="mb-3 grid grid-cols-2 gap-2 text-center">
            <div>
              <div className="text-sm uppercase text-zinc-400">Erster Schuss</div>
              <div className="text-5xl font-bold tabular-nums text-emerald-300">
                {fmt(firstShotMs)}
              </div>
            </div>
            <div>
              <div className="text-sm uppercase text-zinc-400">Gesamtzeit</div>
              <div className="text-5xl font-bold tabular-nums">
                {phase === 'armed'
                  ? `${(armCountdown / 1000).toFixed(1).replace('.', ',')}…`
                  : phase === 'first-pending' || phase === 'running'
                    ? fmt(now)
                    : fmt(elapsedMs)}
              </div>
            </div>
          </div>

          {/* Phase-aware big buttons */}
          {phase === 'idle' || phase === 'stopped' ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={arm}
                className="rounded-lg bg-emerald-700 px-4 py-5 text-base font-bold text-white hover:bg-emerald-600 active:bg-emerald-800"
              >
                ▶ Start
              </button>
              <button
                onClick={reset}
                disabled={phase === 'idle'}
                className="rounded-lg border border-zinc-600 px-4 py-5 text-base font-bold text-zinc-300 hover:border-zinc-400 disabled:opacity-30"
              >
                ↺ Reset
              </button>
            </div>
          ) : phase === 'armed' ? (
            <button
              onClick={() => setPhase('idle')}
              className="w-full rounded-lg bg-amber-700 px-4 py-5 text-lg font-bold text-white"
            >
              Bereit … {formatMs(armCountdown, 1)} — abbrechen
            </button>
          ) : phase === 'first-pending' ? (
            <button
              onClick={() => void recordFirstShot()}
              className="w-full rounded-lg bg-emerald-600 px-4 py-6 text-xl font-bold text-white hover:bg-emerald-500 active:bg-emerald-700"
            >
              Erster Schuss
            </button>
          ) : (
            <button
              onClick={() => void stopAndSave()}
              className="w-full rounded-lg bg-red-700 px-4 py-6 text-xl font-bold text-white hover:bg-red-600 active:bg-red-800"
            >
              ■ Stop
            </button>
          )}
        </>
      ) : (
        <div className="space-y-3">
          {/* Echo back what's already saved so the referee knows whether
              the timing has been entered. Useful when re-opening the
              score screen mid-round. */}
          {/* Keep this space stable: blur-saving must not move the confirmation
              button between pointer-down and click. */}
          {
            <div className="grid grid-cols-2 gap-2 rounded bg-zinc-900/60 p-2 text-center">
              <div>
                <div className="text-xs uppercase text-zinc-400">Erster Schuss</div>
                <div className="text-3xl font-bold tabular-nums text-emerald-300">
                  {fmt(firstShotMs)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-zinc-400">Gesamtzeit</div>
                <div className="text-3xl font-bold tabular-nums">{fmt(elapsedMs)}</div>
              </div>
            </div>
          }
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-sm text-zinc-400">Erster Schuss (s)</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="z. B. 1,06"
                value={manualFirst}
                onChange={(e) => setManualFirst(e.target.value)}
                onBlur={() => void saveManual().catch(() => {})}
                className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-4 text-4xl font-bold tabular-nums"
              />
            </label>
            <label className="block">
              <span className="text-sm text-zinc-400">Gesamtzeit (s)</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="z. B. 8,23"
                value={manualElapsed}
                onChange={(e) => setManualElapsed(e.target.value)}
                onBlur={() => void saveManual().catch(() => {})}
                className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-4 text-4xl font-bold tabular-nums"
              />
            </label>
          </div>
          <button
            onClick={() => void saveManual().catch(() => {})}
            className="w-full rounded-lg bg-emerald-700 px-4 py-3 text-sm font-bold text-white"
          >
            Speichern
          </button>
          <p className="text-xs text-zinc-500">
            Komma oder Punkt als Dezimaltrenner. Wird auch beim Wechsel zum nächsten Feld
            gespeichert.
          </p>
        </div>
      )}
    </section>
  );
});

interface SgTimerPanelProps {
  assignmentId: string;
  expectedShots: number | null;
  onOpenTimerDebug?: (() => void) | undefined;
  onLeave: () => void;
  existingFirstShotMs: number | null;
  existingElapsedMs: number | null;
}

/**
 * SG-Timer-driven panel embedded in TimingPad. Subscribes to the singleton
 * SgTimerSource via useTimer(); reflects shots in real time and saves
 * `firstShotTimeMs` / `elapsedTimeMs` once the run ends. Owns the
 * "auto-stop on shot N" + "manual stop for variable shot count"
 * branching at the UI level — the source still enforces overflow
 * dropping internally regardless of which UI is consuming it.
 */
function SgTimerPanel({
  assignmentId,
  expectedShots,
  onOpenTimerDebug,
  onLeave,
  existingFirstShotMs,
  existingElapsedMs,
}: SgTimerPanelProps) {
  const { status, isConnected, bleSupported } = useTimerRun(assignmentId);

  if (!bleSupported) {
    return (
      <div className="space-y-3 text-sm text-zinc-300">
        <p className="rounded bg-amber-900/30 px-3 py-2 text-xs text-amber-200">
          Web Bluetooth ist in diesem Browser nicht verfügbar (z.&nbsp;B. Safari/iOS). Bitte
          „Manuell" oder „Stoppuhr" verwenden.
        </p>
        <button
          onClick={onLeave}
          className="rounded border border-zinc-600 px-3 py-2 text-xs text-zinc-300 hover:border-zinc-400"
        >
          ← Zurück zu Manuell
        </button>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="space-y-3 text-sm text-zinc-300">
        <p className="rounded bg-zinc-800/60 px-3 py-2 text-xs text-zinc-400">
          Kein SG Timer gekoppelt. Pairing erfolgt einmalig im Timer-Bildschirm.
        </p>
        <button
          onClick={() => onOpenTimerDebug?.()}
          className="w-full rounded-lg bg-emerald-700 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-600"
        >
          Timer koppeln →
        </button>
      </div>
    );
  }

  // Read-only mirror of the source. Start/Stop now live above the
  // keypad on the Score screen so the referee never has to switch
  // tabs mid-run; this tab is just for inspecting splits.
  const firstShot = status.shots[0];
  const lastShot = status.shots[status.shots.length - 1];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 text-center">
        <div>
          <div className="text-sm uppercase text-zinc-400">Erster Schuss</div>
          <div className="text-5xl font-bold tabular-nums text-emerald-300">
            {fmt(firstShot?.tFromStartMs ?? existingFirstShotMs)}
          </div>
        </div>
        <div>
          <div className="text-sm uppercase text-zinc-400">
            {expectedShots != null
              ? `Schuss ${status.shots.length} / ${expectedShots}`
              : 'Letzter Schuss'}
          </div>
          <div className="text-5xl font-bold tabular-nums">
            {fmt(lastShot?.tFromStartMs ?? existingElapsedMs)}
          </div>
        </div>
      </div>

      {status.shots.length > 0 ? (
        <ul className="max-h-48 overflow-auto rounded border border-zinc-800 bg-zinc-950/40 p-2 font-mono text-[11px] text-zinc-400">
          {status.shots.map((s) => (
            <li key={s.ord} className="flex justify-between">
              <span>#{s.ord}</span>
              <span className="tabular-nums">{formatMs(s.tFromStartMs)}</span>
              <span className="tabular-nums text-zinc-600">
                {s.splitMs != null ? `+${formatMs(s.splitMs)}` : '—'}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rounded bg-zinc-900/60 px-3 py-2 text-xs text-zinc-400">
          Noch keine Schüsse erfasst. Runde mit dem Timer-Start oben über der Tastatur starten.
        </p>
      )}
      <p className="text-xs text-zinc-500">
        {expectedShots != null
          ? `Auto-Stopp bei Schuss ${expectedShots}. Überschuss wird ignoriert.`
          : 'IPSC / variable Schusszahl — Runde mit „Timer Stop" über der Tastatur beenden.'}
      </p>
    </div>
  );
}

interface PhoneTimerPanelProps {
  expectedShots: number | null;
  onLeave: () => void;
  onSave: (patch: { firstShotTimeMs?: number; elapsedTimeMs?: number }) => Promise<void>;
  existingFirstShotMs: number | null;
  existingElapsedMs: number | null;
  setFirstShotMs: (ms: number | null) => void;
  setElapsedMs: (ms: number | null) => void;
}

type PhoneSubPhase =
  | { kind: 'no-cal' }
  | { kind: 'calibrating' }
  | { kind: 'ready' }
  | { kind: 'connect-error'; message: string };

/**
 * Phone-mic timer panel embedded in TimingPad. Owns its own
 * `PhoneTimerSource` instance — the singleton SG provider has nothing
 * to share since BLE and mic-based timers are mutually exclusive
 * (only one can drive the run at a time). On leaving the tab the
 * source is torn down and the mic permission released.
 *
 * Always requires a fresh-enough calibration; the wizard caches its
 * output to localStorage so subsequent visits skip straight to
 * `ready`.
 */
function PhoneTimerPanel({
  expectedShots,
  onLeave,
  onSave,
  existingFirstShotMs,
  existingElapsedMs,
  setFirstShotMs,
  setElapsedMs,
}: PhoneTimerPanelProps) {
  const [calibration, setCalibration] = useState<PhoneTimerCalibration | null>(() =>
    loadPhoneTimerCalibration(),
  );
  const [subPhase, setSubPhase] = useState<PhoneSubPhase>(() =>
    loadPhoneTimerCalibration() ? { kind: 'ready' } : { kind: 'no-cal' },
  );
  const sourceRef = useRef<PhoneTimerSource | null>(null);
  const [status, setStatus] = useState<TimerStatus>({
    state: 'idle',
    startedAt: null,
    shots: [],
  });
  const [level, setLevel] = useState<PhoneTimerLevel | null>(null);
  const [applied, setApplied] = useState<AppliedAudioConstraints | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const savedRef = useRef(false);

  // Connect on entry to the 'ready' sub-phase, disconnect on leave.
  useEffect(() => {
    if (subPhase.kind !== 'ready') return;
    let cancelled = false;
    let cleanupStatus: (() => void) | undefined;
    let cleanupLevel: (() => void) | undefined;
    (async () => {
      try {
        const src = new PhoneTimerSource({
          expectedShots: expectedShots ?? null,
        });
        if (calibration) src.applyCalibration(calibration);
        const r = await src.connect();
        if (cancelled) {
          await src.disconnect();
          return;
        }
        sourceRef.current = src;
        setApplied(r.applied);
        cleanupStatus = src.on(setStatus);
        cleanupLevel = src.onLevel(setLevel);
      } catch (err) {
        const msg = userErrorText(err instanceof Error ? err : 'mic_connect_failed', 'mic');
        if (!cancelled) setSubPhase({ kind: 'connect-error', message: msg });
      }
    })();
    return () => {
      cancelled = true;
      cleanupStatus?.();
      cleanupLevel?.();
      const src = sourceRef.current;
      sourceRef.current = null;
      if (src) void src.disconnect();
    };
  }, [subPhase.kind, calibration, expectedShots]);

  // Persist firstShot/elapsed exactly once when the run stops with shots.
  useEffect(() => {
    if (status.state !== 'stopped' || savedRef.current) return;
    const first = status.shots[0];
    const last = status.shots[status.shots.length - 1];
    if (!first || !last) return;
    savedRef.current = true;
    const patch: { firstShotTimeMs?: number; elapsedTimeMs?: number } = {
      firstShotTimeMs: first.tFromStartMs,
      elapsedTimeMs: last.tFromStartMs,
    };
    setFirstShotMs(first.tFromStartMs);
    setElapsedMs(last.tFromStartMs);
    void onSave(patch);
  }, [status, onSave, setFirstShotMs, setElapsedMs]);

  if (subPhase.kind === 'calibrating') {
    return (
      <PhoneTimerCalibrationScreen
        onCancel={() =>
          setSubPhase(loadPhoneTimerCalibration() ? { kind: 'ready' } : { kind: 'no-cal' })
        }
        onDone={(cal) => {
          setCalibration(cal);
          setSubPhase({ kind: 'ready' });
        }}
      />
    );
  }

  if (subPhase.kind === 'no-cal') {
    return (
      <div className="space-y-3 text-sm text-zinc-300">
        <p className="rounded bg-amber-900/30 px-3 py-2 text-xs text-amber-200">
          Beta — nur fürs Solo-Training. Vor der ersten Runde einmal kalibrieren.
        </p>
        <button
          onClick={() => setSubPhase({ kind: 'calibrating' })}
          className="w-full rounded-lg bg-emerald-700 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-600"
        >
          Kalibrierung starten →
        </button>
        <button
          onClick={onLeave}
          className="w-full rounded border border-zinc-600 px-3 py-2 text-xs text-zinc-300 hover:border-zinc-400"
        >
          ← Zurück zu Manuell
        </button>
      </div>
    );
  }

  if (subPhase.kind === 'connect-error') {
    return (
      <div className="space-y-3 text-sm text-red-300">
        <p className="rounded bg-red-900/30 px-3 py-2">{subPhase.message}</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setSubPhase({ kind: 'ready' })}
            className="rounded-lg bg-emerald-700 px-3 py-2 text-xs font-bold text-white"
          >
            ↺ Erneut
          </button>
          <button
            onClick={onLeave}
            className="rounded border border-zinc-600 px-3 py-2 text-xs text-zinc-300 hover:border-zinc-400"
          >
            Zurück
          </button>
        </div>
      </div>
    );
  }

  const firstShot = status.shots[0];
  const lastShot = status.shots[status.shots.length - 1];
  const isRunning = status.state === 'running' || status.state === 'armed';

  async function start() {
    const src = sourceRef.current;
    if (!src) return;
    savedRef.current = false;
    setBusy('start');
    try {
      await src.start();
    } finally {
      setBusy(null);
    }
  }
  async function stop() {
    const src = sourceRef.current;
    if (!src) return;
    setBusy('stop');
    try {
      await src.stop();
    } finally {
      setBusy(null);
    }
  }
  function reset() {
    sourceRef.current?.reset();
    savedRef.current = false;
  }

  return (
    <div className="space-y-3">
      <div className="rounded bg-amber-900/20 px-3 py-2 text-[11px] text-amber-200">
        🎙 Beta — Solo-Training. Andere Schützen am Stand verfälschen die Erkennung.
      </div>

      <div className="grid grid-cols-2 gap-2 text-center">
        <div>
          <div className="text-sm uppercase text-zinc-400">Erster Schuss</div>
          <div className="text-5xl font-bold tabular-nums text-emerald-300">
            {fmt(firstShot?.tFromStartMs ?? existingFirstShotMs)}
          </div>
        </div>
        <div>
          <div className="text-sm uppercase text-zinc-400">
            {expectedShots != null
              ? `Schuss ${status.shots.length} / ${expectedShots}`
              : status.state === 'armed'
                ? 'Bereit…'
                : 'Letzter Schuss'}
          </div>
          <div className="text-5xl font-bold tabular-nums">
            {status.state === 'armed' ? '⏳' : fmt(lastShot?.tFromStartMs ?? existingElapsedMs)}
          </div>
        </div>
      </div>

      {level && (
        <div className="space-y-1">
          <div className="h-2 overflow-hidden rounded-full bg-zinc-800">
            <div className="relative h-full">
              <div
                className="absolute h-full bg-zinc-600"
                style={{ width: `${Math.min(100, level.noiseFloor * 400)}%` }}
              />
              <div
                className="absolute h-full bg-emerald-500"
                style={{ width: `${Math.min(100, level.rms * 400)}%` }}
              />
              <div
                className="absolute h-full w-0.5 bg-amber-300"
                style={{ left: `${Math.min(100, level.threshold * 400)}%` }}
              />
            </div>
          </div>
          <div className="font-mono text-[10px] text-zinc-500">
            rms {(level.rms * 100).toFixed(1)}% · floor {(level.noiseFloor * 100).toFixed(2)}% ·
            trig {(level.threshold * 100).toFixed(2)}%
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        {!isRunning ? (
          <button
            onClick={() => void start()}
            disabled={busy != null}
            className="col-span-2 rounded-lg bg-emerald-700 px-3 py-3 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-30"
          >
            ▶ Start (zufälliges Startsignal 1–4 s)
          </button>
        ) : (
          <button
            onClick={() => void stop()}
            disabled={busy != null}
            className="col-span-2 rounded-lg bg-red-700 px-3 py-3 text-sm font-bold text-white hover:bg-red-600 disabled:opacity-30"
          >
            ■ Stop
          </button>
        )}
        <button
          onClick={reset}
          disabled={busy != null || isRunning}
          className="rounded border border-zinc-600 px-3 py-3 text-sm text-zinc-300 hover:border-zinc-400 disabled:opacity-30"
        >
          ↺ Reset
        </button>
      </div>

      {status.shots.length > 0 ? (
        <ul className="max-h-48 overflow-auto rounded border border-zinc-800 bg-zinc-950/40 p-2 font-mono text-[11px] text-zinc-400">
          {status.shots.map((s) => (
            <li key={s.ord} className="flex justify-between">
              <span>#{s.ord}</span>
              <span className="tabular-nums">{formatMs(s.tFromStartMs)}</span>
              <span className="tabular-nums text-zinc-600">
                {s.splitMs != null ? `+${formatMs(s.splitMs)}` : '—'}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rounded bg-zinc-900/60 px-3 py-2 text-xs text-zinc-400">
          Noch keine Schüsse erkannt.
        </p>
      )}

      <div className="grid grid-cols-2 gap-2 text-xs">
        <button
          onClick={() => setSubPhase({ kind: 'calibrating' })}
          className="rounded border border-zinc-700 px-3 py-2 text-zinc-400 hover:border-zinc-500"
        >
          ⚙ Neu kalibrieren
        </button>
        <button
          onClick={onLeave}
          className="rounded border border-zinc-700 px-3 py-2 text-zinc-400 hover:border-zinc-500"
        >
          ← Zurück zu Manuell
        </button>
      </div>

      {applied &&
        (applied.echoCancellation || applied.autoGainControl || applied.noiseSuppression) && (
          <p className="rounded bg-amber-900/30 px-3 py-2 text-[11px] text-amber-200">
            Browser hält Audio-Verarbeitung aktiv (
            {[
              applied.echoCancellation && 'echo',
              applied.autoGainControl && 'AGC',
              applied.noiseSuppression && 'noise',
            ]
              .filter(Boolean)
              .join('+')}
            ) — Genauigkeit reduziert.
          </p>
        )}
    </div>
  );
}
